import React, { useState, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import ReadOnlyCard from './ReadOnlyCard';
import ReadOnlyExpandableCard from './ReadOnlyCard/ReadOnlyExpandableCard';
import EditCard from './EditCard';

const useStyles = makeStyles(theme => ({
  root: {},
  card: {
    paddingBottom: theme.spacing(2)
  },
  contentRoot: {},
  title: {}
}));

const injectPropsIntoChildrenOf = (
  children,
  wrapperDisplayName,
  injectedProps
) => {
  if (
    children === null ||
    children === undefined ||
    typeof children !== 'object'
  ) {
    return children;
  }

  return React.Children.map(children, child => {
    if (!React.isValidElement(child)) return child;

    const isTargetWrapper = child.type.displayName === wrapperDisplayName;

    if (isTargetWrapper) {
      let didInject = false;

      const newChildren = React.Children.map(
        child.props.children,
        innerChild => {
          if (!React.isValidElement(innerChild)) return innerChild;

          const shouldInject = Object.entries(injectedProps).some(
            // eslint-disable-next-line max-nested-callbacks
            ([key, val]) => innerChild.props[key] !== val
          );

          if (!shouldInject) return innerChild;

          didInject = true;

          return React.cloneElement(innerChild, {
            ...innerChild.props,
            ...injectedProps
          });
        }
      );

      return didInject
        ? React.cloneElement(child, child.props, newChildren)
        : child;
    }

    const updatedChildren = injectPropsIntoChildrenOf(
      child.props.children,
      wrapperDisplayName,
      injectedProps
    );

    return updatedChildren !== child.props.children
      ? React.cloneElement(child, child.props, updatedChildren)
      : child;
  });
};

export const onSave = (baseOnSave, onStopEdit, setSaving) => async () => {
  setSaving(true);
  const result = await baseOnSave();

  setSaving(false);
  // This is hack to avoid breaking EditableCard behaviour in ProjectInfo and ResourcePool etc. baseOnSave promise should return true or false.
  // Once all baseOnSave changed to return true or false. It should be changed to if(result)
  if (result !== false) {
    onStopEdit();
  }
};

const assignProps = (target, props) => ({
  ...(target || {}),
  ...(props || {})
});

const onCancel = ({
  baseOnCancel,
  onStopEdit,
  onCloseSummary,
  editing
}) => () => {
  if (editing) {
    baseOnCancel();
    onStopEdit();
  } else {
    onCloseSummary();
  }
};

const EditableCard = ({
  editable,
  edit = false,
  classes: classesOverride,
  dialogActionClasses,
  dataQeId,
  className,
  editContentClassName,
  fullScreen,
  maxWidth,
  children,
  dialogClasses,
  ariaDialogLabelId,
  ariaLabelKey,
  ariaLabel = null
}) => {
  const classes = useStyles({ classes: classesOverride });

  const [editing, setEditing] = useState(edit);
  const [isSaving, setSaving] = useState(false);
  const [viewSummary, setViewSummary] = useState(false);
  const onEdit = useCallback(() => setEditing(true), []);
  const onStopEdit = useCallback(() => setEditing(false), []);
  const onViewSummary = useCallback(() => setViewSummary(true), []);
  const onCloseSummary = useCallback(() => setViewSummary(false), []);
  const { formatMessage } = useIntl();

  const dialogAriaLabel =
    ariaLabel ||
    formatMessage({
      id: ariaLabelKey
    });
  const editIconAriaLabel = value =>
    formatMessage(
      {
        id: 'button.editButton'
      },
      {
        editButtonLabel: value
      }
    );

  const open = editing || viewSummary;

  return (
    <div className={classNames(className, classes.card)} data-qe-id={dataQeId}>
      {React.Children.toArray(children)
        .filter(child => child.type.displayName === 'ReadOnly')
        .map(child => {
          const ReadOnlyVariant = child.props.expandable
            ? ReadOnlyExpandableCard
            : ReadOnlyCard;

          return (
            <ReadOnlyVariant
              className={classNames(
                className,
                classes.contentRoot,
                classes.title
              )}
              key={child.type.displayName}
              editable={editable}
              onEdit={onEdit}
              onViewSummary={onViewSummary}
              headerButtons={child.headerButtons}
              ariaLabel={editIconAriaLabel(dialogAriaLabel)}
              {...child.props}
            >
              {injectPropsIntoChildrenOf(
                child.props.children,
                'ReadOnlyContent',
                { onEdit, onViewSummary }
              )}
            </ReadOnlyVariant>
          );
        })}
      {open &&
        React.Children.toArray(children)
          .filter(child => child.type.displayName === 'Edit')
          .map(child => (
            <EditCard
              key={child.type.displayName}
              open={open}
              readOnly={viewSummary}
              saveable={child.props.saveable}
              isSaving={isSaving}
              fullScreen={fullScreen}
              maxWidth={maxWidth}
              className={editContentClassName}
              classes={dialogActionClasses}
              onSave={onSave(child.props.onSave, onStopEdit, setSaving)}
              onCancel={onCancel({
                baseOnCancel: child.props.onCancel,
                onStopEdit,
                onCloseSummary,
                editing
              })}
              validateForm={child.props.validateForm}
              onRemove={child.props.onRemove}
              actionLabel={child.props.actionLabel}
              actionComponentCustomProps={assignProps(
                child.props.actionComponentCustomProps,
                { readOnly: viewSummary }
              )}
              ActionComponent={child.props.ActionComponent}
              dialogClasses={dialogClasses}
              ariaLabel={dialogAriaLabel}
              ariaDialogLabelId={ariaDialogLabelId}
            >
              {injectPropsIntoChildrenOf(child.props.children, 'EditContent', {
                readOnly: viewSummary
              })}
            </EditCard>
          ))}
    </div>
  );
};

EditableCard.propTypes = {
  editable: PropTypes.bool,
  edit: PropTypes.bool,
  dataQeId: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  fullScreen: PropTypes.bool,
  maxWidth: PropTypes.any,
  classes: PropTypes.object,
  editContentClassName: PropTypes.string,
  dialogActionClasses: PropTypes.object,
  actionComponentCustomProps: PropTypes.bool,
  dialogClasses: PropTypes.object,
  ariaDialogLabelId: PropTypes.string,
  ariaLabelKey: PropTypes.string,
  ariaLabel: PropTypes.string
};

export default EditableCard;
